import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLocation, useNavigate, type Location } from 'react-router-dom';
import { loginSchema, type LoginCredentials } from '@features/auth/model/auth.types';
import { useLogin } from '@features/auth/model/use-auth';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { PasswordInput } from '@shared/ui/PasswordInput';
import { Alert } from '@shared/ui/Alert';
import { env } from '@shared/config/env';
import { DEFAULT_PATH, paths } from '@app/router/paths';

interface RedirectState {
  from?: Location;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit((credentials) => {
    login.mutate(credentials, {
      onSuccess: () => {
        // Return the admin to whatever they were trying to reach, falling back
        // to the default section.
        const from = (location.state as RedirectState | null)?.from?.pathname;
        navigate(from ?? DEFAULT_PATH, { replace: true });
      },
    });
  });

  return (
    <div className="w-full max-w-sm">
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-semibold text-slate-900">{env.appName}</h1>
        <p className="mt-1 text-sm text-slate-500">Sign in to continue</p>
      </div>

      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {login.isError && <Alert tone="error">{login.error.message}</Alert>}

        <Input
          label="Email"
          type="email"
          autoComplete="username"
          autoFocus
          placeholder="admin@something.com"
          error={errors.email?.message}
          {...register('email')}
        />

        <div>
          <PasswordInput
            label="Password"
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password')}
          />
          <p className="mt-1.5 text-right">
            <Link
              to={paths.forgotPassword}
              className="text-xs text-brand-600 transition-colors hover:underline"
            >
              Forgot password?
            </Link>
          </p>
        </div>

        <Button type="submit" size="lg" isLoading={login.isPending} className="w-full">
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <p className="mt-6 text-center text-xs text-slate-400">
        Access is granted by an administrator. There is no self sign-up.
      </p>
    </div>
  );
}
